import React, { useCallback, useRef, useState } from 'react';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import { Box, Typography, IconButton, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { rqx } from '../../../../../../../../../../core/request/API';
import { env } from '../../../../../../../../../../core/Env';
import GoogleMapsAutocomplete from './GoogleMapsAutocomplete';

const libraries = ["places"];
const containerStyle = {
  position:'relative',
  width: '100%',
  height: '500px',
};

// Default to Manila, Philippines
const defaultPhilippinesCenter = {
  lat: 14.5995, // Manila, Philippines
  lng: 120.9842
};

const getGeolocation = () => {
  let position = null;
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        position = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
      }
    );
  }
  return position;
}

async function getAddressFromLatLng(lat, lng, apiKey) {
  // const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`;
  try {
    const data = await rqx.post(`<<tool>>/${env()}/template/google_geocode_address?lat=${lat}&lng=${lng}`)
    if (data.results && data.results.length > 0) {
      const result = data.results[0];
      const components = result.address_components;

      // Helper to get component by type
      const getComponent = (type) =>
        components.find((c) => c.types.includes(type))?.long_name || '';

      // Extract fields
      const street = `${getComponent('street_number')} ${getComponent('route')}`;
      const area = getComponent('sublocality_level_1') || getComponent('neighborhood');
      const province = getComponent('administrative_area_level_1');
      const city =  getComponent('administrative_area_level_2');
      const barangay =  getComponent('sublocality_level_1') || getComponent('neighborhood') || getComponent('political');
          
      return {
        street,
        area,
        province,
        city,
        barangay,
        formatted_address: result.formatted_address,
        place_id: result.place_id,
        lat,
        lng,
      };
    }
    return null;
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
}

const mapModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 1000,
  height: '100%',
  maxWidth: '95vw',
  maxHeight: '70vh',
  bgcolor: 'background.paper',
  borderRadius: 3,
  boxShadow: 24,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

export default function GoogleMapPicker({ onLocationSelect, initialLocation, onClose }) {
  const [marker, setMarker] = useState(initialLocation || defaultPhilippinesCenter);
  const [center, setCenter] = useState(initialLocation || defaultPhilippinesCenter);
  const inputRef = useRef(null);

  React.useEffect(() => {
    if (!initialLocation) {
      const userLocation = getGeolocation();
      if (userLocation) {
            setCenter(userLocation);
            setMarker(userLocation);
            if (onLocationSelect) {
              onLocationSelect(userLocation);
            }
      }
    }
  }, [initialLocation, onLocationSelect]);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
    libraries
  });


  const handleMapClick = useCallback(
    async (event) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      setMarker({ lat, lng });
      if (onLocationSelect) {
        onLocationSelect({ lat, lng });
      }
    },
    [onLocationSelect]
  );
  
  React.useEffect(() => {
    if (isLoaded && inputRef.current) {
      const newAutocomplete = new window.google.maps.places.Autocomplete(inputRef.current);

      newAutocomplete.addListener('place_changed', () => {
        const place = newAutocomplete.getPlace();
        if (place.geometry && place.geometry.location) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
      
          const newLocation = { lat, lng };
          setMarker(newLocation);
          setCenter(newLocation);
      
          if (onLocationSelect) {
            onLocationSelect(newLocation);
          }
        }
      });
      
    }
  }, [isLoaded]);

  React.useEffect(() => {
    if (marker) {
      setCenter(marker);
    }
  }, [marker]);
  if (!isLoaded) return <div>Loading Map...</div>;

  return (
    <Box sx={mapModalStyle}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#222' }}>
          Choose pickup address from the map
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box p={2}>
        <GoogleMapsAutocomplete setMarker={setMarker} onLocationSelect={onLocationSelect}/>
      </Box>
      <Divider />
      <Box sx={{ flex: 1 }}>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={25}
          onClick={handleMapClick}
        >
          {marker && (
            <Marker
              position={marker}
              animation={window.google.maps.Animation?.DROP}
            />
          )}
        </GoogleMap>
      </Box>
    </Box>
  );
}

export { getAddressFromLatLng };
